import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setModal, selectModal } from "../../../redux/modalSlice"
import { useRequest } from '../../../hooks/UseRequest'
import { useResendCooldown } from '../../../hooks/UseResendCooldown'
import { setNotification } from '../../../redux/notificationSlice'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faCopy, faRefresh, faPaperPlane, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import Loading from '../../../components/Loading'
import { FormatISODateTime } from '../../../helpers/formatting'
import { Link } from 'react-router-dom'
import { handleToolTipMouseEnter } from '../../../helpers/layout'

export default function BankLoginsModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const request = useRequest();
  const dropdownOptions = useSelector(selectDropdownOptions);
  const customerId = modal.options?.customerId;

  const [loading, setLoading] = useState(false);
  const [bankAccountsData, setBankAccountsData] = useState([]); // Full bank account data with transactions
  const [bankAccounts, setBankAccounts] = useState([]); // Options for the select dropdown
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [loginRequests, setLoginRequests] = useState([]);
  const [copyingRequestId, setCopyingRequestId] = useState(null);
  const [sendingBankLogin, setSendingBankLogin] = useState(false);
  const [resendingRequestId, setResendingRequestId] = useState(null);
  const [refreshingRequestId, setRefreshingRequestId] = useState(null);
  const isRefreshingRef = useRef(false);
  const sendingBankLoginRef = useRef(false);
  const resendingRef = useRef(false);
  const { startCooldown, isCoolingDown } = useResendCooldown();
  const requestedCustomerId = useRef(null);
  // Bumped on every open/close so a late response from an earlier opening is dropped, even for the same customer
  const openSession = useRef(0);
  const active = modal.is_active && modal.type === 'bank_logins_modal';

  // Processing status dropdown options for bank login requests
  const processingStatusOptions = Object.values(dropdownOptions).filter((option) =>
    option.code_key &&
    (option.code_key.startsWith('processing_status') || option.code_key.startsWith('bank_transaction_provider_call_outcome')));

  // Helper function to get status label from outcome_status_dropdown_option_id
  const getStatusLabel = (statusId) => {
    const statusOption = processingStatusOptions.find((option) => option.id === statusId);

    if (!statusOption) return 'Unknown'

    switch (statusOption.code_key) {
      case 'processing_status_pending':
        return 'Not Started'
      case 'processing_status_processing':
        return 'Started'
      case 'processing_status_completed':
        return 'Completed'
      case 'bank_transaction_provider_call_outcome_decisionlogic_unsupported_bank':
        return 'Unsupported Bank'
      case 'bank_transaction_provider_call_outcome_decisionlogic_approved':
        return 'Approved'
      case 'bank_transaction_provider_call_outcome_decisionlogic_declined':
        return 'Declined'
      default:
        return statusOption.name || 'Unknown'
    }
  };

  // Resolve the provider dropdown option (Chirp vs DecisionLogic) for a bank login request
  const getProviderOption = (request) => {
    if (request.provider_ddo) return request.provider_ddo
    if (request.provider_dropdown_option_id && dropdownOptions) {
      return Object.values(dropdownOptions).find((opt) => opt?.id === request.provider_dropdown_option_id) || null
    }
    return null
  };

  // Fetch customer bank transactions (includes bank accounts with nested transactions and provider calls)
  const getCustomerBankTransactions = async () => {
    if (!customerId) return

    const forCustomerId = customerId
    const isStale = () => requestedCustomerId.current !== forCustomerId
    requestedCustomerId.current = forCustomerId
    setLoading(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/bank_transactions`)
      const response = await request.get()
      if (isStale()) return

      if (response.status === 200 || response.status === 201) {
        const accountsData = response?.data?.data || []
        setBankAccountsData(accountsData)

        // Create bank account options for the dropdown
        const bankAccountOptions = accountsData.map((account) => {
          const label = `${account.bank_name || 'Bank'} ****${account.bank_account_number_last_four || ''}`
          return {
            label: label,
            value: account.id,
            account: account
          }
        })

        setBankAccounts(bankAccountOptions)
      }
    } catch (error) {
      if (isStale()) return
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message ?? 'Failed to fetch customer bank transactions' }))
    } finally {
      if (!isStale()) setLoading(false)
    }
  };

  // Extract provider calls (bank logins) from selected bank account's transactions
  const extractLoginRequestsFromBankAccount = (bankAccountId) => {
    if (!bankAccountId || !bankAccountsData.length) {
      setLoginRequests([])
      return
    }

    const selectedAccount = bankAccountsData.find((account) => account.id === bankAccountId)
    if (!selectedAccount || !selectedAccount.bank_transaction || !Array.isArray(selectedAccount.bank_transaction)) {
      setLoginRequests([])
      return
    }

    // Extract provider calls from all bank transactions for this account
    const providerCalls = selectedAccount.bank_transaction
      .filter((transaction) => transaction.bank_transaction_provider_calls)
      .map((transaction) => {
        const providerCall = transaction.bank_transaction_provider_calls
        // Include bank_change_id from the parent transaction if available
        return {
          ...providerCall,
          bank_change_id: transaction.bank_change_id,
          bank_transaction_id: transaction.id
        }
      })

    setLoginRequests(providerCalls)
  };

  const resetState = () => {
    requestedCustomerId.current = null
    openSession.current += 1
    isRefreshingRef.current = false
    sendingBankLoginRef.current = false
    resendingRef.current = false
    setLoading(false)
    setBankAccountsData([])
    setBankAccounts([])
    setSelectedBankAccount(null)
    setLoginRequests([])
    setCopyingRequestId(null)
    setSendingBankLogin(false)
    setResendingRequestId(null)
    setRefreshingRequestId(null)
  };

  // This modal stays mounted in the Footer between openings, so clear the previous customer's bank
  // accounts before loading — otherwise a login request could be sent for the wrong customer's account.
  useEffect(() => {
    if (active) {
      resetState()
      getCustomerBankTransactions()
    }
  }, [active, customerId]);

  // Extract login requests when bank account is selected
  useEffect(() => {
    // Skip rebuilding if we're in the middle of a refresh to prevent overwriting updates
    if (isRefreshingRef.current) {
      return
    }
    if (selectedBankAccount) {
      extractLoginRequestsFromBankAccount(selectedBankAccount.value)
    } else {
      setLoginRequests([])
    }
  }, [selectedBankAccount, bankAccountsData]);

  const handleClose = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  }

  const handleBankAccountChange = (selectedOption) => {
    setSelectedBankAccount(selectedOption)
  };

  const copyToClipboard = async (link, requestId) => {
    setCopyingRequestId(requestId)
    try {
      await navigator.clipboard.writeText(link)
      dispatch(setNotification({ type: 'success', body: 'Link copied to clipboard!' }))
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = link
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      dispatch(setNotification({ type: 'success', body: 'Link copied to clipboard!' }))
    } finally {
      setTimeout(() => setCopyingRequestId(null), 500)
    }
  };

  const refreshSingleRequest = async (providerCallId) => {
    if (!providerCallId || !selectedBankAccount?.value) return

    const forSession = openSession.current
    const isStale = () => openSession.current !== forSession
    // Read from the current render rather than inside the setLoginRequests updater — React doesn't
    // guarantee the updater runs before the bankAccountsData sync below reads it.
    const currentItem = loginRequests.find((req) => req.id === providerCallId)
    setRefreshingRequestId(providerCallId)
    isRefreshingRef.current = true
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_account/${selectedBankAccount.value}/bank_transaction/decision_logic_request_status/${providerCallId}?refresh=true`)
      const response = await request.get()
      if (isStale()) return

      if (response?.data?.data && response.data.data.id) {
        const updatedProviderCall = response.data.data

        if (currentItem) {
          setLoginRequests((prev) =>
            prev.map((req) =>
              (req.id === providerCallId
                ? {
                    ...updatedProviderCall,
                    bank_change_id: currentItem.bank_change_id,
                    bank_transaction_id: currentItem.bank_transaction_id
                  }
                : req)))
        }

        // Update bankAccountsData to keep it in sync (this will trigger useEffect, but we've set the ref to prevent it)
        if (currentItem) {
          setBankAccountsData((prev) =>
            prev.map((account) => {
              if (account.id === selectedBankAccount.value) {
                // Check if the transaction with this provider call exists
                const transactionExists = account.bank_transaction?.some(
                  (transaction) => transaction.bank_transaction_provider_calls?.id === providerCallId
                )

                if (transactionExists) {
                  // Update existing transaction
                  const updatedTransactions = account.bank_transaction?.map((transaction) => {
                    if (transaction.bank_transaction_provider_calls?.id === providerCallId) {
                      return {
                        ...transaction,
                        bank_transaction_provider_calls: updatedProviderCall
                      }
                    }
                    return transaction
                  })
                  return {
                    ...account,
                    bank_transaction: updatedTransactions
                  }
                } else {
                  // Item not in bankAccountsData yet (e.g., added via sendNewBankLogin)
                  // Add it now so it persists through useEffect rebuilds
                  const newTransaction = {
                    id: currentItem.bank_transaction_id || updatedProviderCall.bank_transaction_id || null,
                    bank_transaction_provider_calls: updatedProviderCall,
                    bank_change_id: currentItem.bank_change_id || null
                  }
                  return {
                    ...account,
                    bank_transaction: [...(account.bank_transaction || []), newTransaction]
                  }
                }
              }
              return account
            }))
        }
      }
    } catch (error) {
      if (isStale()) return
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message || 'Failed to refresh bank login request.' }))
    } finally {
      // resetState already cleared the refresh flags when the customer changed
      if (!isStale()) {
        // Use setTimeout to ensure state updates have completed before allowing useEffect to run again
        setTimeout(() => {
          isRefreshingRef.current = false
          setRefreshingRequestId(null)
        }, 0)
      }
    }
  };

  const resendDecisionLogicRequest = async (requestId) => {
    if (!requestId || resendingRef.current || isCoolingDown(customerId)) return;
    resendingRef.current = true;
    const forSession = openSession.current;
    const isStale = () => openSession.current !== forSession;
    setResendingRequestId(requestId);
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${requestId}/decision_logic_resend_email`);
      const response = await request.post({});
      if (isStale()) return;
      if (response?.data?.success) {
        startCooldown(customerId);
      } else {
        const message = response?.data?.message || 'Failed to resend bank login request.';
        dispatch(setNotification({ type: 'error', body: message }));
      }
    } catch (error) {
      if (isStale()) return;
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message || 'Failed to resend bank login request.' }));
    } finally {
      // resetState already cleared the in-flight flag when the modal reopened
      if (!isStale()) {
        resendingRef.current = false;
        setResendingRequestId(null);
      }
    }
  };

  const sendNewBankLogin = async () => {
    if (!selectedBankAccount?.value || sendingBankLoginRef.current || isCoolingDown(customerId)) return

    sendingBankLoginRef.current = true
    // A late response from an earlier opening must not be appended to the current list
    const forSession = openSession.current
    const isStale = () => openSession.current !== forSession
    setSendingBankLogin(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_account/${selectedBankAccount.value}/bank_transaction/decision_logic_login_request`)
      const response = await request.post({})
      if (isStale()) return

      if (response?.status === 201 && response?.data?.success) {
        // Merge the returned provider call into the current list (no refetch required)
        const data = response?.data?.data
        if (data) {
          setLoginRequests((prev) => [...prev, data])
          startCooldown(customerId)

          // Also add to bankAccountsData so it's available for refresh operations
          // Find or create the transaction entry for this provider call
          setBankAccountsData((prev) =>
            prev.map((account) => {
              if (account.id === selectedBankAccount.value) {
                // Check if there's already a transaction with this provider call
                const existingTransaction = account.bank_transaction?.find(
                  (transaction) => transaction.bank_transaction_provider_calls?.id === data.id
                )

                if (existingTransaction) {
                  // Update existing transaction
                  const updatedTransactions = account.bank_transaction?.map((transaction) =>
                    (transaction.id === existingTransaction.id
                      ? { ...transaction, bank_transaction_provider_calls: data }
                      : transaction))
                  return {
                    ...account,
                    bank_transaction: updatedTransactions
                  }
                } else {
                  // Create a new transaction entry for this provider call
                  const newTransaction = {
                    id: data.bank_transaction_id || null,
                    bank_transaction_provider_calls: data,
                    bank_change_id: null
                  }
                  return {
                    ...account,
                    bank_transaction: [...(account.bank_transaction || []), newTransaction]
                  }
                }
              }
              return account
            }))
        }
        // Only show message if success is false
        if (response?.data?.message && !response?.data?.success) {
          dispatch(setNotification({ type: 'error', body: response.data.message }))
        }
      } else {
        const message = response?.data?.message || 'Failed to send bank login request.'
        dispatch(setNotification({ type: 'error', body: message }))
      }
    } catch (error) {
      if (isStale()) return
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message || 'Failed to send bank login request.' }))
    } finally {
      // resetState already cleared the in-flight flag when the modal reopened
      if (!isStale()) {
        sendingBankLoginRef.current = false
        setSendingBankLogin(false)
      }
    }
  };

  const modalWidthClass = selectedBankAccount && loginRequests.length > 0
    ? 'column is-10-tablet is-8-fullhd is-12-mobile'
    : '';

  return modal.is_active && modal.type === 'bank_logins_modal' ? (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className={`modal-card ${modalWidthClass}`}>
        <header className="modal-card-head py-3">
          <p className="modal-card-title">Bank Logins</p>
          <button className="delete" aria-label="close" onClick={handleClose}></button>
        </header>

        <section className={`modal-card-body pt-1 pb-1 ${loginRequests.length > 0 ? 'lox-modal-body-scroll' : ''}`}>
          {!loading && bankAccounts.length > 0 && (
            <div className="field is-horizontal mb-4 pt-3">
              <label className="label lox-label-min-width-180">Select Bank Account</label>
              <div className="control lox-control-width-400">
                <Select
                  value={selectedBankAccount}
                  onChange={handleBankAccountChange}
                  options={bankAccounts}
                  isSearchable={false}
                  classNames={reactSelectClassNames()}
                  placeholder="Select a bank account..."
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    container: (base) => ({ ...base, width: '100%' }),
                    menu: (css) => ({ ...css, width: '100%' }),
                  }}
                />
              </div>
            </div>
          )}

          {(() => {
            if (loading) {
              return <Loading />
            }

            if (loginRequests.length > 0) {
              return (
                <div className="table-container lox-table-container-min-height">
                  <table className="table is-bordered is-striped is-narrow is-fullwidth">
                      <thead>
                        <tr className="is-selected">
                          <th className="is-selected has-text-centered is-nowrap">Request #</th>
                          <th className="is-selected has-text-centered is-nowrap">Status</th>
                          <th className="is-selected has-text-centered is-nowrap">Action Reason</th>
                          <th className="is-selected has-text-centered is-nowrap">Created At</th>
                          <th className="is-selected has-text-centered is-nowrap">Updated At</th>
                          <th className="is-selected has-text-centered is-nowrap">Bank Change ID</th>
                          <th className="is-selected has-text-centered is-nowrap">Bank Account #</th>
                          <th className="is-selected has-text-centered is-nowrap">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginRequests.map((req) => {
                          const providerOption = getProviderOption(req)
                          const resultsPath = providerOption?.code_key === 'provider_chirp' ? `/chirp_results/${req.id}` : `/decision_logic_results/${req.id}`
                          const decisionLogicLink = `${window.location.origin}${resultsPath}`
                          const status = getStatusLabel(req.outcome_status_dropdown_option_id)
                          const canViewResults = status === 'Completed' || status === 'Approved' || status === 'Declined'

                          return (
                            <tr key={req.id || req.ext_request_code}>
                              <td className="has-text-centered">{req.ext_request_code || req.id}</td>
                              <td className="has-text-centered">
                                <span className="is-inline-flex is-align-items-center">
                                  {status}
                                  {req.last_error_message && (
                                    <span
                                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal ml-1`}
                                      data-tooltip={req.last_error_message}
                                      onMouseEnter={(e) => handleToolTipMouseEnter(e, '.table-container')}
                                    >
                                      <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        className={`pl-1 has-text-link`}
                                      />
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="has-text-centered">{req.action_reason || '-'}</td>
                              <td className="has-text-centered">{FormatISODateTime(req.created_at)}</td>
                              <td className="has-text-centered">{FormatISODateTime(req.updated_at)}</td>
                              <td className="has-text-centered">
                                {req.bank_change_id ? (
                                  <Link to={`/bank_change/${req.bank_change_id}`} target="_blank" rel="noopener noreferrer">
                                    {req.bank_change_id}
                                  </Link>
                                ) : '-'}
                              </td>
                              <td className="has-text-centered">
                                {selectedBankAccount?.account?.id ? (
                                  <Link to={`/bank_account/${selectedBankAccount.account.id}`} target="_blank" rel="noopener noreferrer">
                                    {`${selectedBankAccount.account.bank_name || 'Bank'} ****${selectedBankAccount.account.bank_account_number_last_four || ''}`}
                                  </Link>
                                ) : '-'}
                              </td>
                              <td>
                                {status === 'Unsupported Bank' ? (
                                  <div className="has-text-centered">-</div>
                                ) : (
                                  <div className="buttons is-centered is-flex is-justify-content-space-evenly is-align-items-center">
                                    {(() => {
                                      if (resendingRequestId === req.id) {
                                        return (
                                          <div className="lox-loading-container">
                                            <Loading size="is-size-7" />
                                          </div>
                                        );
                                      }

                                      if (canViewResults) {
                                        return (
                                          <>
                                            <span
                                              className="is-clickable"
                                              onClick={() => window.open(resultsPath, '_blank', 'noopener,noreferrer')}
                                              title="View results"
                                            >
                                              <FontAwesomeIcon icon={faEye} />
                                            </span>
                                            <span
                                              className="is-clickable"
                                              onClick={() => copyToClipboard(decisionLogicLink, req.id)}
                                              title="Copy link to clipboard"
                                            >
                                              <FontAwesomeIcon icon={faCopy} />
                                            </span>
                                          </>
                                        );
                                      }

                                      return (
                                        <>
                                          <button
                                            type="button"
                                            className="button is-white is-small m-0"
                                            onClick={() => resendDecisionLogicRequest(req.id)}
                                            disabled={resendingRequestId !== null || isCoolingDown(customerId)}
                                            title={isCoolingDown(customerId) ? 'Email sent recently' : 'Resend email'}
                                          >
                                            <FontAwesomeIcon icon={faPaperPlane} />
                                          </button>
                                          <span
                                            className="is-clickable"
                                            onClick={() => {
                                              refreshSingleRequest(req.id);
                                            }}
                                            title="Refresh status"
                                          >
                                            <FontAwesomeIcon icon={faRefresh} className={refreshingRequestId === req.id ? 'fa-pulse' : ''} />
                                          </span>
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
              )
            }

            if (selectedBankAccount && !loading) {
              return (
                <div className="has-text-centered py-6">
                  <p className="has-text-grey">No bank login requests found for this account.</p>
                </div>
              )
            }

            return null
          })()}
        </section>

        <footer className="modal-card-foot is-justify-content-end py-1">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button
              type="button"
              className="button is-link is-small"
              onClick={sendNewBankLogin}
              disabled={sendingBankLogin || !selectedBankAccount || isCoolingDown(customerId)}
              title={isCoolingDown(customerId) ? 'Email sent recently' : undefined}
            >
              {sendingBankLogin ? 'Sending...' : 'Send New Bank Login'}
            </button>
            <button type="button" className="button is-small has-background-grey has-text-white ml-2" onClick={handleClose}>
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  ) : null
}
